// src/components/SiteLayout.jsx – Bootstrap-ready version

import React, { useEffect, useState, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import classNames from "classnames";                // handy for merging classes

import { db } from "../firebase";
import STARRY from "../images/Smoke.gif";           // fallback
import { isAdminExperiencePath } from "../adminShell/adminRouteConfig";

export default function SiteLayout({ children }) {
  // ---------------------------------------------------------------------------
  //  Routing helpers – suppress Black-Box banner on /admin pages only.
  // ---------------------------------------------------------------------------
  const { pathname } = useLocation();
  const isAdminRoute = isAdminExperiencePath(pathname);
  const isBeatPage = pathname.startsWith("/beats/");

  // ---------------------------------------------------------------------------
  //  Firestore appearance doc → live site-wide theming
  // ---------------------------------------------------------------------------
  const [appearance, setAppearance] = useState({
    parallaxAssets : [],
    cycleParallax  : false,
    globalFont     : "",
    primaryColor   : "#ffffff",
    secondaryColor : "#000000",
    enableGradient : false,
    gradientCSS    : "",
  });
  const [parallaxReady, setParallaxReady] = useState(false);

  useEffect(
    () =>
      onSnapshot(doc(db, "appSettings", "siteAppearance"), snap => {
        if (!snap.exists()) return;
        const d = snap.data();
        setAppearance({
          parallaxAssets : d.parallaxAssets   || [],
          cycleParallax  : d.cycleParallax    ?? false,
          globalFont     : d.globalFont       || "",
          primaryColor   : d.primaryColor     || "#ffffff",
          secondaryColor : d.secondaryColor   || "#000000",
          enableGradient : d.enableGradient   || false,
          gradientCSS    : d.gradientCSS      || "",
        });
      }),
    []
  );

  // Delay heavy parallax assets until after the splash finishes (or a short fallback).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let t;
    const markReady = () => setParallaxReady(true);
    window.addEventListener("vkz-loading-done", markReady, { once: true });
    if (document.readyState === "complete") {
      t = setTimeout(markReady, 4000);
    } else {
      window.addEventListener(
        "load",
        () => { t = setTimeout(markReady, 4000); },
        { once: true }
      );
    }
    return () => {
      window.removeEventListener("vkz-loading-done", markReady);
      clearTimeout(t);
    };
  }, []);

  // ---------------------------------------------------------------------------
  //  Parallax / cycling logic
  // ---------------------------------------------------------------------------
  const [selectedIndex,  setSelectedIndex]  = useState(0);
  const [currentAsset,   setCurrentAsset]   = useState({ url: STARRY, type: "image" });
  const [overlayVis,     setOverlayVis]     = useState(false);
  const [scrollY,        setScrollY]        = useState(0);

  // scroll listener → parallax shift (throttled to reduce input jank)
  useEffect(() => {
    if (!parallaxReady) return undefined;
    let raf = 0;
    let last = window.scrollY || window.pageYOffset || 0;
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      if (Math.abs(y - last) < 6) return;
      last = y;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrollY(last);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [parallaxReady]);

  // pick first asset when firebase finishes
  useEffect(() => {
    if (!parallaxReady) return;
    if (appearance.parallaxAssets.length && currentAsset.url === STARRY) {
      setCurrentAsset(appearance.parallaxAssets[0]);
    }
  }, [appearance.parallaxAssets, currentAsset.url, parallaxReady]);

  // 3-min cycling (if enabled) + immediate kick
  const kickedRef = useRef(false);
  useEffect(() => {
    const canCycle = parallaxReady && appearance.cycleParallax && appearance.parallaxAssets.length >= 2;
    if (!canCycle) {
      // reset the "kick" gate when disabling / not enough assets
      kickedRef.current = false;
      return;
    }

    const len = appearance.parallaxAssets.length;

    // 🔸 Immediate kick: advance once right away so it starts cycling instantly
    if (!kickedRef.current) {
      setSelectedIndex(i => (i + 1) % len);
      kickedRef.current = true;
    }

    // 🔸 Continue cycling every 3 minutes (180,000 ms)
    const id = setInterval(() => {
      setSelectedIndex(i => (i + 1) % len);
    }, 180_000);

    // cleanup: clear interval and allow re-kick if deps change
    return () => {
      clearInterval(id);
      kickedRef.current = false;
    };
  }, [appearance.cycleParallax, appearance.parallaxAssets.length, parallaxReady]);

  // cross-fade between assets
  useEffect(() => {
    if (!parallaxReady) return;
    const next = appearance.parallaxAssets[selectedIndex];
    if (next && next.url !== currentAsset.url) {
      setOverlayVis(true);
      const t = setTimeout(() => {
        setCurrentAsset(next);
        setOverlayVis(false);
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [selectedIndex, appearance.parallaxAssets, currentAsset.url, parallaxReady]);

  // ---------------------------------------------------------------------------
  //  Helper – produce an <img>/<video> with all the crop / offset props baked in
  // ---------------------------------------------------------------------------
  const buildAssetElement = a => {
    if (!a) return null;

    /* read & fallback props */
    const rotate  = a.rotate       ?? 0;
    const scale   = (a.scale ?? 100) / 100;
    const offX    = a.offsetX      ?? 0;
    const offY    = a.offsetY      ?? 0;
    const speed   = a.parallaxSpeed?? 0.3;   // 0–1 (fraction of scroll)

    const crop  = a.crop ?? { x: 0, y: 0 };
    const hasCrop     = Boolean(a.crop);
    const hasVignette = typeof a.vignetteSize === "number" && typeof a.vignetteSoftness === "number";

    // wrapper (applies overflow hidden when cropping)
    const wrapCss = {
      position : "absolute",
      inset    : 0,
      overflow : hasCrop ? "hidden" : "visible",
    };

    // transform chain
    const parallaxShift = scrollY * speed;
    const baseTx = `translate(-50%, -50%) translate(${offX}%, ${offY}%) translateY(${parallaxShift}px) rotate(${rotate}deg) scale(${scale})`;
    const transform = hasCrop ? `${baseTx} translate(${-crop.x}px, ${-crop.y}px)` : baseTx;

    const mediaCss = {
      position   : "absolute",
      top        : "50%",
      left       : "50%",
      minWidth   : "100%",
      minHeight  : "100%",
      objectFit  : "cover",
      transform,
      willChange : "transform",
    };

    const vignette = hasVignette ? (
      <div
        style={{
          position:"absolute", inset:0, pointerEvents:"none",
          background:`radial-gradient(circle,
              rgba(0,0,0,0) ${a.vignetteSize}%,
              rgba(0,0,0,0.7) ${Math.min(100, a.vignetteSize + a.vignetteSoftness)}%,
              rgba(0,0,0,1) 100%)`,
        }}
      />
    ) : null;

    return (
      <div style={wrapCss}>
        {a.type === "video" ? (
          <video src={a.url} autoPlay muted loop playsInline style={mediaCss} />
        ) : (
          <img
            src={a.url}
            alt="parallax"
            style={mediaCss}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        )}
        {vignette}
      </div>
    );
  };

  const backgroundCss = {
    position       : "fixed",
    top            : 0,
    left           : 0,
    width          : "100%",
    height         : "100vh",
    zIndex         : -100,
    pointerEvents  : "none",
  };

  const overlayCss = {
    position  : "absolute",
    inset     : 0,
    background: "black",
    transition: "opacity 4s ease",
    opacity   : overlayVis ? 1 : 0,
  };

  const contentClass = classNames(
    "min-vh-100 w-100 position-relative d-flex flex-column", // Bootstrap helpers
    { "text-white" : appearance.secondaryColor.toLowerCase() === "#ffffff" }
  );

  const customFont = appearance.globalFont ? { fontFamily: appearance.globalFont } : {};

  // ---------------------------------------------------------------------------
  //  RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* 1️⃣  Visual Black-Box & refresh strip (skip on /admin) */}
      {!isAdminRoute && !isBeatPage && (
        <>
          <div className="blackbox-overlay"          style={{ pointerEvents: "none" }} />
          <div className="banner-reload-hitbox"     onClick={() => window.location.reload()} />
        </>
      )}

      {/* 2️⃣  Parallax background layer */}
      <div style={backgroundCss}>
        {buildAssetElement(currentAsset)}
        <div style={overlayCss} />
      </div>

      {/* 3️⃣  Main page content   */}
      <div className={contentClass} style={customFont}>
        {children}
      </div>
    </>
  );
}
